import Backbone from 'backbone';

import {assertAndContinue} from 'Asserts';
import {Side} from 'constants/MolbioConstants';

class Trim extends Backbone.Model {
  override defaults(): object {
    return {
      start: null,
      end: null,
      sequence: null,
      draggingType: null,
    };
  }

  override initialize(): void {
    this.listenTo(this.get('sequence').get('_sequence'), 'inserted', this.handleInsert.bind(this));
    this.listenTo(this.get('sequence').get('_sequence'), 'deleted', this.handleDelete.bind(this));
  }

  handleInsert(pos: number, bases: string, shouldUpdate: boolean = true): void {
    if (pos < this.start()) {
      this.set({start: this.start() + bases.length});
    }
    if (pos < this.end()) {
      this.set({end: this.end() + bases.length});
    }
    if (shouldUpdate) {
      this.triggerUpdate();
    }
  }

  handleDelete(start: number, end: number, shouldUpdate: boolean = true): void {
    const length = end - start;
    if (end < this.end()) {
      this.set({end: this.end() - length});
    } else if (start < this.end()) {
      this.set({end: start});
    }

    if (end < this.start()) {
      this.set({start: this.start() - length});
    } else if (start < this.start()) {
      this.set({start});
    }
    if (shouldUpdate) {
      this.triggerUpdate();
    }
  }

  start(): number {
    return this.get('start');
  }
  end(): number {
    return this.get('end');
  }

  startDrag(type: Side): void {
    if (this.get('sequence').allowFeatureEdits()) {
      this.set({draggingType: type});
    }
  }

  setStart(index: number, trigger?: boolean): void {
    if (trigger == null) {
      trigger = true;
    }
    this.set({start: Math.min(index, this.end())});
    if (trigger) {
      this.triggerUpdate();
    }
  }

  setEnd(index: number, trigger?: boolean): void {
    if (trigger == null) {
      trigger = true;
    }
    this.set({end: Math.max(index, this.start())});
    if (trigger) {
      this.triggerUpdate();
    }
  }

  setBoth(start: number, end: number, trigger?: boolean): void {
    if (trigger == null) {
      trigger = true;
    }
    if (start > end) {
      [start, end] = Array.from([end, start]);
    }
    this.set('start', start);
    this.set('end', end);
    if (trigger) {
      this.triggerUpdate();
    }
  }

  drag(index: number): void {
    const draggingType = this.get('draggingType');
    assertAndContinue(this.isActive(), 'Dragging type is not set while dragging trim!');
    assertAndContinue(['start', 'end'].includes(draggingType), `Unexpected dragging type: ${draggingType}`);

    if (draggingType === 'start') {
      this.setStart(index, false);
    } else {
      this.setEnd(index, false);
    }
  }

  isActive(): boolean {
    return this.get('draggingType') != null;
  }

  stopDrag(): void {
    if (this.isActive()) {
      this.set({draggingType: null});
      this.triggerUpdate();
      this.renderLineRanges();
    }
  }

  renderLineRanges(): void {
    this.get('sequence').get('_sequence').trigger('invalidateLineRanges');
    this.get('sequence').get('_sequence').trigger('renderLineRanges');
  }

  triggerUpdate(): void {
    this.get('sequence').get('_sequence').trigger('updateTrim', this.start(), this.end());
  }
}

export default Trim;
